import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Pagination,
  Grid,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Category as CategoryIcon,
  SubdirectoryArrowRight as SubcategoryIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { categoryService } from '../services';

const Categories = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [hierarchicalCategories, setHierarchicalCategories] = useState([]);
  const [parentCategories, setParentCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCategories: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'hierarchical'
  const [expandedCategories, setExpandedCategories] = useState({});

  useEffect(() => {
    fetchCategories();
    fetchParentCategories();
    fetchHierarchicalCategories();
  }, [pagination.currentPage, searchTerm, sortBy, sortOrder]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.currentPage,
        limit: 10,
        sort: sortBy,
        order: sortOrder
      });
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }

      const response = await categoryService.getCategories(params);
      setCategories(response.categories);
      setPagination(response.pagination);
    } catch (error) {
      toast.error('Failed to fetch categories');
    } finally {
      setLoading(false);
    }
  };

  const fetchParentCategories = async () => {
    try {
      const response = await categoryService.getParentCategories();
      setParentCategories(response);
    } catch (error) {
      console.error('Failed to fetch parent categories:', error);
      toast.error('Failed to fetch parent categories');
    }
  };

  const fetchHierarchicalCategories = async () => {
    try {
      const response = await categoryService.getHierarchical();
      setHierarchicalCategories(response);
    } catch (error) {
      console.error('Failed to fetch hierarchical categories:', error);
    }
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      try {
        await categoryService.deleteCategory(categoryId);
        toast.success('Category deleted successfully');
        fetchCategories();
        fetchParentCategories();
        fetchHierarchicalCategories();
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to delete category');
      }
    }
  };

  const toggleExpanded = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const handleEditCategory = (category) => {
    navigate('/categories/edit', { state: { category } });
  };

  const openCreateDialog = () => {
    navigate('/categories/add');
  };

  const renderHierarchicalView = () => {
    return (
      <Paper sx={{ mt: 2 }}>
        <List>
          {hierarchicalCategories.map((parent) => (
            <React.Fragment key={parent._id}>
              <ListItem
                button
                onClick={() => toggleExpanded(parent._id)}
                sx={{ 
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  mb: 1,
                  bgcolor: '#f5f5f5'
                }}
              >
                <ListItemIcon>
                  <CategoryIcon />
                </ListItemIcon>
                <ListItemText 
                  primary={parent.title}
                  secondary={`Parent Category • ${parent.subcategories?.length || 0} subcategories`}
                />
                {expandedCategories[parent._id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </ListItem>
              <Collapse in={expandedCategories[parent._id]} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {parent.subcategories?.map((subcategory) => (
                    <ListItem
                      key={subcategory._id}
                      sx={{ 
                        pl: 4,
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        mb: 0.5,
                        bgcolor: '#fafafa'
                      }}
                    >
                      <ListItemIcon>
                        <SubcategoryIcon />
                      </ListItemIcon>
                      <ListItemText 
                        primary={subcategory.title}
                        secondary="Subcategory"
                      />
                      <Box>
                        <IconButton
                          size="small"
                          onClick={() => handleEditCategory(subcategory)}
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteCategory(subcategory._id)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </React.Fragment>
          ))}
        </List>
      </Paper>
    );
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Category Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreateDialog}
        >
          Add Category
        </Button>
      </Box>

      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Search categories"
            value={searchTerm}
            onChange={handleSearch}
            placeholder="Search by name or description..."
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            select
            label="Sort By"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            SelectProps={{ native: true }}
          >
            <option value="title">Title</option>
            <option value="createdAt">Created Date</option>
          </TextField>
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            select
            label="Order"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            SelectProps={{ native: true }}
          >
            <option value="asc">Ascending</option>
            <option value="desc">Descending</option>
          </TextField>
        </Grid>
      </Grid>

      {loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Image</TableCell>
                  <TableCell>
                    <Button onClick={() => handleSort('title')}>
                      Title {sortBy === 'title' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </Button>
                  </TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>
                    <Button onClick={() => handleSort('createdAt')}>
                      Created {sortBy === 'createdAt' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </Button>
                  </TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {categories.map((category) => (
                  <TableRow key={category._id}>
                    <TableCell>
                      {category.image ? (
                        <img
                          src={`${process.env.REACT_APP_UPLOAD_URL}/${category.image}`}
                          alt={category.title}
                          style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4 }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: 50,
                            height: 50,
                            bgcolor: 'grey.200',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: 1
                          }}
                        >
                          <ImageIcon color="disabled" />
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>{category.title}</TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {category.description}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={category.isActive ? 'Active' : 'Inactive'}
                        color={category.isActive ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(category.createdAt).toLocaleDateString("en-IN", {
                        timeZone: "Asia/Kolkata"
                      })}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => handleEditCategory(category)}
                        color="primary"
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDeleteCategory(category._id)}
                        color="error"
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box display="flex" justifyContent="center" mt={3}>
            <Pagination
              count={pagination.totalPages}
              page={pagination.currentPage}
              onChange={(event, page) => setPagination(prev => ({ ...prev, currentPage: page }))}
              color="primary"
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default Categories;